/**
 * HiBFF — Google Play Billing (LIVE implementation, Aug 2026)
 *
 * Native in-app purchases via @capgo/native-purchases inside the Capacitor
 * Android shell. The backend (/api/play/verify) is the source of truth:
 * a purchase only grants Hearts / a tier after Google-side verification.
 *
 * On web (or if billing is unavailable) every function reports unavailable
 * so callers fall back to Stripe web checkout — same contract as before.
 */
import { Capacitor } from '@capacitor/core';
import apiClient from '../api/client';

// Play Console product ids — must match backend PRODUCTS map exactly.
const HEARTS_PRODUCTS = {
  50: 'hibff_hearts_50',
  200: 'hibff_hearts_200',
  500: 'hibff_hearts_500',
};
const PLAN_PRODUCTS = {
  starter: { id: 'hibff_starter_monthly', basePlan: 'monthly' },
  monthly: { id: 'hibff_plus_monthly', basePlan: 'monthly' },
  annual: { id: 'hibff_plus_annual', basePlan: 'annual' },
};

const isNativeAndroid = () =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

let _plugin = null;
async function getPlugin() {
  if (_plugin) return _plugin;
  const mod = await import('@capgo/native-purchases');
  _plugin = mod;
  return mod;
}

export function isPlayBillingAvailable() {
  return isNativeAndroid();
}

export async function isPlayBillingReady() {
  if (!isNativeAndroid()) return false;
  try {
    const { NativePurchases } = await getPlugin();
    const s = await NativePurchases.isBillingSupported();
    if (!s.isBillingSupported) return false;
    const server = await apiClient.request('/api/play/status');
    return !!server.configured;
  } catch {
    return false;
  }
}

export async function getPlayCatalog() {
  if (!isNativeAndroid()) return null;
  const { NativePurchases, PURCHASE_TYPE } = await getPlugin();
  const inapp = await NativePurchases.getProducts({
    productIdentifiers: Object.values(HEARTS_PRODUCTS),
    productType: PURCHASE_TYPE.INAPP,
  });
  const subs = await NativePurchases.getProducts({
    productIdentifiers: Object.values(PLAN_PRODUCTS).map((p) => p.id),
    productType: PURCHASE_TYPE.SUBS,
  });
  return { hearts: inapp.products || [], plans: subs.products || [] };
}

async function verifyOnServer(productId, kind, purchaseToken) {
  return apiClient.request('/api/play/verify', {
    method: 'POST',
    body: JSON.stringify({ product_id: productId, kind, purchase_token: purchaseToken }),
  });
}

export async function purchaseHeartsPack(packSize) {
  if (!isNativeAndroid()) return { status: 'unavailable' };
  const productId = HEARTS_PRODUCTS[packSize];
  if (!productId) return { status: 'error', message: `Unknown hearts pack: ${packSize}` };
  try {
    const { NativePurchases, PURCHASE_TYPE } = await getPlugin();
    const tx = await NativePurchases.purchaseProduct({
      productIdentifier: productId,
      productType: PURCHASE_TYPE.INAPP,
      autoAcknowledgePurchases: false,
    });
    if (!tx.purchaseToken) return { status: 'error', message: 'Missing Google purchase token' };
    const result = await verifyOnServer(productId, 'inapp', tx.purchaseToken);
    return { status: 'credited', message: `+${result.hearts} Hearts added!`, ...result };
  } catch (e) {
    const msg = String(e?.message || e);
    if (/cancel/i.test(msg)) return { status: 'cancelled' };
    return { status: 'error', message: 'Purchase failed — please try again' };
  }
}

export async function purchasePlusPlan(interval = 'monthly') {
  if (!isNativeAndroid()) return { status: 'unavailable' };
  const plan = PLAN_PRODUCTS[interval];
  if (!plan) return { status: 'error', message: `Unknown plan interval: ${interval}` };
  try {
    const { NativePurchases, PURCHASE_TYPE } = await getPlugin();
    const tx = await NativePurchases.purchaseProduct({
      productIdentifier: plan.id,
      productType: PURCHASE_TYPE.SUBS,
      planIdentifier: plan.basePlan,
      autoAcknowledgePurchases: false,
    });
    if (!tx.purchaseToken) return { status: 'error', message: 'Missing Google purchase token' };
    const result = await verifyOnServer(plan.id, 'subscription', tx.purchaseToken);
    return { status: 'active', message: 'Plan activated!', ...result };
  } catch (e) {
    const msg = String(e?.message || e);
    if (/cancel/i.test(msg)) return { status: 'cancelled' };
    return { status: 'error', message: `Google Play purchase failed: ${msg.slice(0, 140)}` };
  }
}

export async function restorePlayPurchases() {
  if (!isNativeAndroid()) return { available: false, restored: 0 };
  const { NativePurchases, PURCHASE_TYPE } = await getPlugin();
  try { await NativePurchases.restorePurchases(); } catch { /* non-fatal */ }
  const { purchases } = await NativePurchases.getPurchases({
    productType: PURCHASE_TYPE.SUBS,
  });
  let restored = 0;
  for (const p of purchases || []) {
    if (p.purchaseToken) {
      try {
        await verifyOnServer(p.productIdentifier, 'subscription', p.purchaseToken);
        restored += 1;
      } catch { /* token may be expired — backend is authoritative */ }
    }
  }
  return { available: true, restored };
}
